<!--
  @component ReleaseIO — P3 effecting lens (m-arc).
  chain:   ctx.deployment.dispatch("release.*", …) → this → ctx.release
  effects: release / cutover / drain / rollbackApp dispatches.
           Unwired ⇒ NotWiredError (FACE-LEDGER).
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { RELEASE_IO_KEY } from "./deployment-io.js";
  import type { ReleaseIOContext } from "./deployment-io.js";
  export { RELEASE_IO_KEY };
  export type { ReleaseIOContext };
  export function useReleaseIO(): ReleaseIOContext {
    const ctx = getContext<ReleaseIOContext | undefined>(RELEASE_IO_KEY);
    if (!ctx) throw new Error("useReleaseIO() called outside of a ReleaseIO tree.");
    return ctx;
  }
</script>
<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";
  import type { DeployTargetRef } from "./deployment-io.js";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastResult: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function dispatchOp(op: string, payload: unknown): Promise<unknown> {
    inFlight = true; lastError = null;
    try {
      const r = await parent.dispatch(op, payload);
      lastResult = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally { inFlight = false; }
  }

  const ctx: ReleaseIOContext = {
    get inFlight() { return inFlight; },
    get lastResult() { return lastResult; },
    get lastError() { return lastError; },
    get history() { return parent.stream.filter((e) => e.op.startsWith("release.")); },
    release: (target: DeployTargetRef, options?) =>
      dispatchOp("release.run", { target, options }),
    cutover: (target, options?) => dispatchOp("release.cutover", { target, options }),
    drain: (target, options?) => dispatchOp("release.drain", { target, options }),
    rollbackApp: (target, options?) => dispatchOp("release.rollback", { target, options }),
  } satisfies ReleaseIOContext;

  setContext(RELEASE_IO_KEY, ctx);
</script>
{@render children?.()}
